import { WillData } from './schema';

export interface SectionedWillData {
  about: {
    personalDetails: WillData['personalDetails'];
    hasChildren?: boolean;
    children?: WillData['children'];
    hasPets?: boolean;
    pets?: WillData['pets'];
  };
  guardians: {
    appointGuardians?: boolean;
    guardians?: WillData['guardians'];
    appointPetGuardians?: boolean;
    petGuardians?: WillData['petGuardians'];
    guardianConfirmations?: WillData['guardianConfirmations'];
  };
  estate: {
    beneficiaries: WillData['beneficiaries'];
    gifts?: WillData['gifts'];
    estateCompleted?: boolean;
    disasterFallback?: WillData['disasterFallback'];
  };
  executors: {
    executors: WillData['executors'];
    substituteExecutors?: WillData['substituteExecutors'];
  };
  optionals: {
    funeralWishes?: string;
    exclusions?: WillData['exclusions'];
    excludeChoice?: string | null;
    exclusionMessage?: string;
    additionalInstructions?: string;
  };
  // Keep other top-level fields or grouping them into 'metadata'
  metadata: {
    status?: string;
    manuallyAddedPeople?: WillData['manuallyAddedPeople'];
  };
}

export function transformToSectioned(flat: WillData): any {
  // We return 'any' or a looser type because the DB might store extra fields we don't know about
  // but strictly speaking it returns SectionedWillData
  return {
    about: {
      personalDetails: flat.personalDetails,
      hasChildren: flat.hasChildren,
      children: flat.children,
      hasPets: flat.hasPets,
      pets: flat.pets,
    },
    guardians: {
      appointGuardians: flat.appointGuardians,
      guardians: flat.guardians,
      appointPetGuardians: flat.appointPetGuardians,
      petGuardians: flat.petGuardians,
      guardianConfirmations: flat.guardianConfirmations,
    },
    estate: {
      beneficiaries: flat.beneficiaries,
      gifts: flat.gifts,
      estateCompleted: flat.estateCompleted,
      disasterFallback: flat.disasterFallback,
    },
    executors: {
      executors: flat.executors,
      substituteExecutors: flat.substituteExecutors,
    },
    optionals: {
      funeralWishes: flat.funeralWishes,
      exclusions: flat.exclusions,
      excludeChoice: flat.excludeChoice,
      exclusionMessage: flat.exclusionMessage,
      additionalInstructions: flat.additionalInstructions,
    },
    // Top-level status
    status: flat.status,
    metadata: {
      manuallyAddedPeople: flat.manuallyAddedPeople,
    },
  };
}

export function transformToFlat(sectioned: any): WillData {
  // Handle legacy flat data (if it doesn't have the 'about' section, assume it's flat)
  if (!sectioned || !sectioned.about) {
    return sectioned as WillData;
  }

  return {
    // About
    personalDetails: sectioned.about.personalDetails,
    hasChildren: sectioned.about.hasChildren,
    children: sectioned.about.children,
    hasPets: sectioned.about.hasPets,
    pets: sectioned.about.pets,

    // Guardians
    appointGuardians: sectioned.guardians.appointGuardians,
    guardians: sectioned.guardians.guardians,
    appointPetGuardians: sectioned.guardians.appointPetGuardians,
    petGuardians: sectioned.guardians.petGuardians,
    guardianConfirmations: sectioned.guardians.guardianConfirmations,

    // Estate
    beneficiaries: sectioned.estate.beneficiaries,
    gifts: sectioned.estate.gifts,
    estateCompleted: sectioned.estate.estateCompleted,
    disasterFallback: sectioned.estate.disasterFallback,

    // Executors
    executors: sectioned.executors.executors,
    substituteExecutors: sectioned.executors.substituteExecutors,

    // Optionals
    funeralWishes: sectioned.optionals.funeralWishes,
    exclusions: sectioned.optionals.exclusions,
    excludeChoice: sectioned.optionals.excludeChoice,
    exclusionMessage: sectioned.optionals.exclusionMessage,
    additionalInstructions: sectioned.optionals.additionalInstructions,

    // Metadata / Top Level
    status: sectioned.status || sectioned.metadata?.status,
    manuallyAddedPeople: sectioned.metadata?.manuallyAddedPeople || [],
  } as WillData;
}

